// src/pages/candidate/CandidateProfile.tsx
// 🎨 GRID LAYOUT: Smart grouping with responsive columns
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GraduationCap,
    Clock,
    Calendar,
    MapPin,
    Save
} from 'lucide-react';
import { useCandidateProfile } from '@/contexts/CandidateProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { CandidateProfileUpdatePayload } from '@/lib/models/candidateProfile';
import { CandidateProfileService } from '@/services/CandidateProfileService';

// Import Nexilink components
import { LoadingState, ErrorState } from '@/components/custom-ui/empty-state';
import { ActionButton } from '@/components/custom-ui/action-button';
import { IconChip } from '@/components/custom-ui/IconChip';
import { FloatingActionBar } from '@/components/custom-ui/FloatingActionBar';
import CvUploadCard from '@/components/dashboard/candidate/profile/CvUploadCard';
import TagInput from '@/components/custom-ui/TagInput';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Card styles
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING, NEXILINK_GAP } from '@/lib/nexilink-card-styles';

const CandidateProfile: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { userProfile, isLoadingProfile, updateUserProfile, isSavingProfile } = useCandidateProfile();

    // Form state
    const [formData, setFormData] = useState<Partial<CandidateProfileUpdatePayload>>({
        university: '',
        education_status: undefined,
        other_education: '',
        min_hours_per_week: 15,
        max_hours_per_week: 25,
        available_from_date: '',
        location: '',
        address: '',
        postal_code: ''
    });

    const [hasChanges, setHasChanges] = useState(false);
    const [universities, setUniversities] = useState<string[]>([]);
    const [locations, setLocations] = useState<string[]>([]);

    // Load profile data when available
    useEffect(() => {
        if (userProfile) {
            const formatDateForInput = (dateString: string | undefined | null): string => {
                if (!dateString) return '';
                return dateString.split('T')[0];
            };

            setFormData({
                university: userProfile.university || '',
                education_status: userProfile.education_status,
                other_education: userProfile.other_education || '',
                min_hours_per_week: userProfile.min_hours_per_week || 15,
                max_hours_per_week: userProfile.max_hours_per_week || 25,
                available_from_date: formatDateForInput(userProfile.available_from_date),
                location: userProfile.location || '',
                address: userProfile.address || '',
                postal_code: userProfile.postal_code || ''
            });
        }
    }, [userProfile]);

    // Load universities and locations
    useEffect(() => {
        loadUniversities();
        loadLocations();
    }, []);

    const loadUniversities = async () => {
        try {
            const results = await CandidateProfileService.searchUniversities('');
            setUniversities(results.map(u => u.name));
        } catch (error) {
            console.error('Error loading universities:', error);
        }
    };

    const loadLocations = async () => {
        try {
            const results = await CandidateProfileService.searchLocations('');
            setLocations(results.map(l => l.name));
        } catch (error) {
            console.error('Error loading locations:', error);
        }
    };

    const updateField = (updates: Partial<CandidateProfileUpdatePayload>) => {
        setFormData(prev => ({ ...prev, ...updates }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            // Validation
            // #298 — education is optional. Left required here, a candidate who
            // signed up after the education step was removed could not save ANY
            // profile edit without first answering a question the product stopped
            // asking. The 'other' rule still applies when a status IS chosen.
            if (formData.education_status === 'other' && !formData.other_education?.trim()) {
                throw new Error('Vänligen ange vilken utbildning du har');
            }
            if (!formData.min_hours_per_week || !formData.max_hours_per_week) {
                throw new Error('Arbetstimmar är obligatoriska');
            }
            if (!formData.available_from_date) {
                throw new Error('Tillgänglighetsdatum är obligatoriskt');
            }
            if (!formData.location) {
                throw new Error('Plats är obligatorisk');
            }

            const payload: CandidateProfileUpdatePayload = {
                ...formData,
                other_education: formData.education_status === 'other' ? formData.other_education : undefined,
            };
            const result = await updateUserProfile(payload);

            if (result) {
                toast({
                    title: 'Profil uppdaterad!',
                    description: 'Dina ändringar har sparats.',
                    duration: 3000
                });
                setHasChanges(false);
            }
        } catch (error: any) {
            toast({
                title: 'Fel vid sparande',
                description: error.message || 'Kunde inte spara profilen',
                variant: 'destructive',
                duration: 5000
            });
        }
    };

    const today = new Date().toISOString().split('T')[0];
    const minHours = formData.min_hours_per_week || 15;
    const maxHours = formData.max_hours_per_week || 25;

    if (isLoadingProfile) {
        return <LoadingState message="Laddar profil..." />;
    }

    if (!userProfile) {
        return (
            <ErrorState
                title="Ingen profil hittades"
                message="Du behöver skapa en profil först."
            />
        );
    }

    return (
        <div className={NEXILINK_SPACING}>
            {/* #359 — the "Utbildning" card (Universitet / Högskola + the
                socionomexamen question) stood here. Removed: signup stopped
                collecting education in #298, so this page asked for something the
                product no longer wants, in the student framing the pivot left
                behind. DB columns + API fields are untouched — an existing value
                still shows to employers and to Link, and restoring the card is
                UI-only work. */}

            {/* #359 — the CV leads the page now. It was last, below the fold on a
                short viewport, which is how a candidate could finish signup, be told
                "Profil komplett", and never discover that uploading one is possible.
                It is also the only thing on this page an employer reads in depth. */}
            {/* SECTION: CV upload (#47a) — self-contained, uploads immediately
                (independent of the form save below). */}
            <CvUploadCard />

            {/* SECTION 2: Work Details (2-Column Grid) */}
            <div className={`grid grid-cols-1 lg:grid-cols-2 ${NEXILINK_GAP}`}>
                {/* Work Hours */}
                <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
                    <div className="flex items-center gap-3 mb-6">
                        <IconChip icon={Clock} size="lg" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Arbetstimmar</h2>
                            <p className="text-sm text-gray-500">Per vecka</p>
                        </div>
                    </div>

                    {/* #359 — was a 4xl number in a p-6 block, which made this card
                        roughly twice the height of Tillgänglighet beside it and left a
                        large hole in the grid. Same information, one line. */}
                    <div className="bg-[#A4C3B2]/25 rounded-xl px-4 py-3 mb-5 text-center">
                        <span className="text-xl font-bold text-[#1A4D4F]">{minHours}–{maxHours}</span>
                        <span className="text-sm text-gray-600 ml-2">timmar per vecka</span>
                    </div>

                    {/* Min Hours Slider */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-700">
                                Minimum
                            </label>
                            <span className="text-sm font-bold text-[#1A4D4F]">
                                {minHours} timmar
                            </span>
                        </div>
                        <input
                            type="range"
                            min="1"
                            max="40"
                            step="1"
                            value={minHours}
                            onChange={(e) => {
                                const newMin = parseInt(e.target.value);
                                if (newMin <= maxHours) {
                                    updateField({ min_hours_per_week: newMin });
                                }
                            }}
                            className="slider-teal w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>1h</span>
                            <span>40h</span>
                        </div>
                    </div>

                    {/* Max Hours Slider */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-700">
                                Maximum
                            </label>
                            <span className="text-sm font-bold text-[#1A4D4F]">
                                {maxHours} timmar
                            </span>
                        </div>
                        <input
                            type="range"
                            min="1"
                            max="40"
                            step="1"
                            value={maxHours}
                            onChange={(e) => {
                                const newMax = parseInt(e.target.value);
                                if (newMax >= minHours) {
                                    updateField({ max_hours_per_week: newMax });
                                }
                            }}
                            className="slider-teal w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>1h</span>
                            <span>40h</span>
                        </div>
                    </div>
                </div>

                {/* Availability & Location */}
                <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
                    <div className="flex items-center gap-3 mb-6">
                        <IconChip icon={Calendar} size="lg" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Tillgänglighet</h2>
                            <p className="text-sm text-gray-500">När och var kan du arbeta?</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Available From Date */}
                        <div>
                            <Label htmlFor="available-from" className="mb-2 block">Tillgänglig från</Label>
                            <Input
                                id="available-from"
                                type="date"
                                value={formData.available_from_date}
                                onChange={(e) => updateField({ available_from_date: e.target.value })}
                                min={today}
                            />
                        </div>

                        {/* Location */}
                        <div>
                            <Label className="mb-2 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                Plats
                            </Label>
                            <TagInput
                                value={formData.location ? [formData.location] : []}
                                onChange={(tags) => updateField({ location: tags[0] || '' })}
                                suggestions={locations}
                                placeholder="Välj eller skriv din stad"
                                maxItems={1}
                                showHelperText={false}
                            />
                        </div>

                    </div>
                </div>
            </div>


            {/* Save button — inline, floats to bottom when scrolled past */}
            <FloatingActionBar>
                <ActionButton
                    onClick={handleSave}
                    disabled={!hasChanges}
                    loading={isSavingProfile}
                    icon={Save}
                    className="flex-1"
                >
                    {hasChanges ? 'Spara ändringar' : 'Inga ändringar att spara'}
                </ActionButton>
            </FloatingActionBar>

            {/* CSS for custom slider */}
            <style>{`
                .slider-teal::-webkit-slider-thumb {
                    appearance: none;
                    width: 24px;
                    height: 24px;
                    background: #1A4D4F;
                    cursor: pointer;
                    border-radius: 50%;
                    box-shadow: 0 2px 8px rgba(26, 77, 79, 0.4);
                }
                
                .slider-teal::-moz-range-thumb {
                    width: 24px;
                    height: 24px;
                    background: #1A4D4F;
                    cursor: pointer;
                    border-radius: 50%;
                    border: none;
                    box-shadow: 0 2px 8px rgba(26, 77, 79, 0.4);
                }
            `}</style>
        </div>
    );
};

export default CandidateProfile;